import { Enquiry } from "../components/Enquiry";
import { Footer } from "../components/Footer";
import { Heading } from "../components/Heading/Heading";
import { Layout } from "../components/Layout";
import {
  InfoSection,
  InfoSectionCtaContainer,
  InfoSectionMediaContainer,
  InfoSectionParagraph,
  InfoSectionTextContainer,
} from "../components/Sections/InfoSection";
import { LogosSection } from "../components/Sections/LogosSection";
import { SignupCTAAlt } from "../components/Sections/SignupCTAAlt";
import { useIsMobile } from "../hooks/useIsMobile";
import { FAQAccordionControlled } from "./FAQ";
import { DiscountSwitch } from "./PricingPlans/DiscountSwitch/DiscountSwitch";
import { FeaturesMatrix } from "./PricingPlans/FeaturesMatrix/FeaturesMatrix";
import * as styles from "./PricingPlans/PricingPageSelect/Select.module.scss";
import { IBillingCycle, PricingPlans } from "./PricingPlans/PricingPlans";
import { SectionWrapper } from "./PricingPlans/SectionWrapper";
import * as Select from "@radix-ui/react-select";
import cx from "classnames";
import { MotionConfig, motion } from "framer-motion";
import { StaticImage } from "gatsby-plugin-image";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Check, ChevronDown } from "react-feather";
import { Helmet } from "react-helmet";

const HEADING_THREE_STYLES =
  "!tw-font-bold !tw-p-0 !tw-mb-0 tw-text-left max-container:!tw-text-center !tw-text-5xl !tw-leading-[1.1] tw-text-white max-container:tw-w-full max-container:!tw-text-4xl max-sm:!tw-text-3xl";
const BUTTON_STYLES = "btn btn-secondary btn-inverse btn-lg !tw-m-0 !tw-font-bold max-sm:!tw-text-sm max-xl:!tw-text-lg";
const MAX_CONTAINER_WIDTH_STYLES = "min-xxxl:!tw-max-w-[2000px] tw-w-full";
export const currencyOptions = [
  { label: "USD", value: "USD" },
  { label: "GBP", value: "GBP" },
  { label: "EUR", value: "EUR" },
  { label: "AUD", value: "AUD" },
];

export const currencyCodes = {
  USD: { name: "USD", label: "USD" },
  GBP: { name: "GBP", label: "GBP" },
  EUR: { name: "EUR", label: "EUR" },
  AUD: { name: "AUD", label: "AUD" },
};

const periods: IBillingCycle[] = ["monthly", "yearly"];
const discountRates = ["20%", "40%"];

export const PricingPageLegacy = ({ location }) => {
  const [period, setPeriod] = React.useState<IBillingCycle>(periods[1]);
  const [discountRate, setDiscountRate] = React.useState(discountRates[0]);
  const [defaultFaqOpen, setDefaultFaqOpen] = React.useState(null);
  const [showDiscount, setShowDiscount] = React.useState(true);
  const [notForProfitActivated, setNotForProfitActivated] = React.useState(false);
  const [showPortal, setShowPortal] = React.useState(false);

  const isMobile = useIsMobile(1100);

  const scrollTargetRef = React.useRef<HTMLDivElement>();
  const prePopulatedContactFormFields = React.useRef({});

  // Function to scroll to the target element
  function scrollToTarget(): void {
    if (scrollTargetRef.current) {
      setDefaultFaqOpen("item_0");
      scrollTargetRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }

  // because we want to control our faq accordion via opening the currencies section programmatically
  function updateControlledSelect(value: string): void {
    setDefaultFaqOpen(value);
  }

  // showing this discount forces all plans into yearly mode
  function handelNotForProfitClicked(value: boolean): void {
    if (notForProfitActivated) {
      // force monthly and 20% discount
      setDiscountRate(discountRates[0]);
      setPeriod(periods[1]);
      setNotForProfitActivated(value);
      return;
    }
    // force yearly and 40% discount
    setDiscountRate(discountRates[1]);
    setPeriod(periods[1]);
    setNotForProfitActivated(true);
    setShowDiscount(true);
  }
  // updates prices and discount labels
  function handlePeriodChange(): void {
    if (period === periods[1]) {
      // monthly plans don't get a discount
      setShowDiscount(false);
      setPeriod(periods[0]);
      setNotForProfitActivated(false);
      return;
    }
    setPeriod(periods[1]);
    setDiscountRate(discountRates[0]);
    setShowDiscount(true);
  }

  function handleBusinessClick(): void {
    setShowPortal(true);
    prePopulatedContactFormFields.current = {
      interestedIn: "setting-up-a-trial",
      message: "Starting a trial of the Business plan",
    };
  }

  function handleEnterpriseClick(): void {
    setShowPortal(true);
    prePopulatedContactFormFields.current = {
      interestedIn: "plans-and-pricing",
      message: "Learning more about the Enterprise plan",
    };
  }

  function handleCharitiesClick(): void {
    setShowPortal(true);
    prePopulatedContactFormFields.current = {
      interestedIn: "plans-and-pricing",
      message: "Learning more about the Charities plan",
    };
  }

  function handleDiscountApplyClick(): void {
    setShowPortal(true);
    prePopulatedContactFormFields.current = {
      interestedIn: "plans-and-pricing",
      message: "Application for discount",
    };
  }

  function handleFAQGetINTouchClick(interestedIn: string, message: string): void {
    setShowPortal(true);
    prePopulatedContactFormFields.current = {
      interestedIn,
      message,
    };
  }

  function pushUserToSignup(): void {
    window.location.assign("https://app.shorthand.com/signup");
  }

  const portalProps = {
    type: "pricing-enquiry",
    subject: "pricing-enquiry",
    source: "marketing/plans",
    onClose: () => setShowPortal(false),
  };

  const duration = 0.8;
  const initial = { opacity: 0, filter: "blur(5px)" };
  const animate = { opacity: 1, filter: "blur(0px)" };

  return (
    <Layout location={location} theme="dark-theme">
      <MotionConfig reducedMotion="user">
        <main
          id="main"
          className="marketing-content tw-flex tw-w-full tw-flex-col tw-items-center tw-bg-black tw-pb-[80px] tw-text-white"
        >
          <Helmet
            bodyAttributes={{
              class: "body-marketing body-shorthand",
            }}
          >
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          {showPortal &&
            ReactDOM.createPortal(
              <Enquiry {...portalProps} prePopulatedFields={prePopulatedContactFormFields.current} />,
              document.getElementById("modal")
            )}

          <div className={cx(MAX_CONTAINER_WIDTH_STYLES, "tw-animate-fadeIn")}>
            <div className="tw-flex tw-flex-col tw-items-center  tw-pt-[111px] tw-text-center">
              <InlineLogo />
              <motion.div
                initial={{ opacity: 0, translateY: 0 }}
                animate={{ opacity: 1, translateY: -3 }}
                transition={{ duration, delay: 0.1 }}
                key={"heading-block-1"}
              >
                <Heading headerType={1} className="tw-text-white">
                  <motion.span initial={{ ...initial }} animate={{ ...animate }} transition={{ duration }}>
                    <PageHeading />
                  </motion.span>
                </Heading>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, translateY: 0 }}
                animate={{ opacity: 1, translateY: -3 }}
                transition={{ duration, delay: 0.3 }}
                key={"heading-block-2"}
                className="max-sm:!tw-max-w-[90%]"
              >
                <Heading
                  headerType={2}
                  className="tw-flex tw-w-full tw-justify-center tw-pt-[18px] !tw-text-center !tw-text-2xl !tw-font-normal !tw-text-grey_scale-200 max-sm:!tw-text-xl"
                >
                  <motion.span initial={{ ...initial }} animate={{ ...animate }} transition={{ duration }}>
                    <PageSubHeading />
                  </motion.span>{" "}
                </Heading>
              </motion.div>

              {!isMobile ? (
                <motion.section
                  initial={{ opacity: 0, translateY: 0 }}
                  animate={{ opacity: 1, translateY: -3 }}
                  transition={{ duration, delay: 0.3 }}
                  key={"logos-block"}
                  className="tw-flex tw-w-full tw-flex-col tw-items-center tw-pb-[80px] tw-pt-[80px] max-sm:!tw-pb-[18px] max-sm:!tw-pt-[18px]"
                >
                  <div className="tw-flex tw-max-w-[65%] tw-justify-center max-sm:tw-max-w-full">
                    <LogosSection />
                  </div>
                </motion.section>
              ) : null}
            </div>

            <motion.div
              initial={{ opacity: 0, translateY: 0 }}
              animate={{ opacity: 1, translateY: -3 }}
              transition={{ duration, delay: 0.4 }}
            >
              <section className="tw-flex tw-w-full tw-flex-col tw-items-center tw-pt-16 min-lg:!tw-pb-[60px]">
                <SectionWrapper>
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <label className={styles.selectLabel}>
                      Prices in
                      <Select.Root value="USD">
                        <Select.Trigger
                          className="tw-flex tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-border-[1px] tw-border-solid tw-border-grey_800 tw-p-2"
                          aria-label="currency selector"
                        >
                          <div className={styles.selectValue}>
                            <Select.Value placeholder={"USD"} />
                          </div>
                          <Select.Icon>
                            <ChevronDown className={styles.selectIconColor} size={20} />
                          </Select.Icon>
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content className={styles.selectContent}>
                            <Select.Viewport className={styles.selectViewport}>
                              <Select.Item className={styles.selectItem} value="USD" asChild>
                                <div style={{ justifyContent: "space-between" }}>
                                  <Select.ItemText>USD</Select.ItemText>
                                  <Select.Icon>
                                    <Check className={styles.tickColor} size={20} />
                                  </Select.Icon>
                                </div>
                              </Select.Item>
                              <Select.Item className={styles.selectItem} key={"FAQ"} value={"FAQ"} asChild>
                                <button onPointerDown={scrollToTarget}>Alternative currencies</button>
                              </Select.Item>
                            </Select.Viewport>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                    </label>
                  </div>
                  <DiscountSwitch
                    id={"discountSwitch"}
                    label="Not-for-profit pricing"
                    labelPosition="right"
                    value={notForProfitActivated}
                    handleFunction={handelNotForProfitClicked}
                  />
                </SectionWrapper>
                <PricingPlans
                  notForProfitActivated={notForProfitActivated}
                  showDiscount={showDiscount}
                  discount={discountRate}
                  currency={currencyCodes.USD}
                  period={period}
                  handlePeriodChange={handlePeriodChange}
                  handleStarterClick={pushUserToSignup}
                  handleTeamClick={pushUserToSignup}
                  handleEnterpriseClick={handleEnterpriseClick}
                  handleBusinessClick={handleBusinessClick}
                />
              </section>
              {isMobile ? (
                <section className="tw-flex tw-w-full tw-flex-col tw-items-center tw-pb-[80px] tw-pt-[80px] max-sm:!tw-pb-[18px] max-sm:!tw-pt-[18px]">
                  <div className="tw-flex tw-max-w-[65%] tw-justify-center max-sm:tw-max-w-full">
                    <LogosSection />
                  </div>
                </section>
              ) : null}
              <section className="tw-flex tw-w-full tw-flex-col tw-items-center tw-pb-[67px] max-sm:!tw-pb-[18px]">
                <FeaturesMatrix
                  handleStarterClick={pushUserToSignup}
                  handleBusinessClick={handleBusinessClick}
                  handleEnterpriseClick={handleEnterpriseClick}
                  handleTeamClick={pushUserToSignup}
                />
              </section>
              {/* Not for profit discounts */}
              <section className="tw-flex tw-w-full tw-flex-row-reverse tw-pb-[102px] tw-pt-[90px] max-container:!tw-flex-col-reverse max-container:!tw-items-center max-container:tw-pt-[30px] max-sm:!tw-pb-[30px] max-sm:tw-pt-[30px]">
                <InfoSection>
                  <Heading headerType={3} className={HEADING_THREE_STYLES}>
                    Not-for-profit and <br />
                    charity <span className="tw-font-PassengerSerif !tw-font-semibold tw-italic">discounts </span>
                  </Heading>
                  <InfoSectionTextContainer>
                    <InfoSectionParagraph>
                      Shorthand helps charities and other non-profits to tell important stories. Inspire your audience to action with
                      powerful editorial content, campaigns, annual reports, and more. Get in touch to apply for our NFP discount.
                    </InfoSectionParagraph>
                  </InfoSectionTextContainer>
                  <InfoSectionCtaContainer>
                    <button data-tracking="enq-cta-01" onClick={handleCharitiesClick} className={BUTTON_STYLES}>
                      Get in touch
                    </button>
                  </InfoSectionCtaContainer>
                </InfoSection>
                <InfoSectionMediaContainer>
                  <div className="max-sm:!tw-rounded-0 max-container:!tw-bg-transparent tw-h-auto !tw-overflow-hidden  tw-pb-10 tw-pt-10 max-xxxl:tw-rounded-r-lg max-container:tw-flex max-container:tw-justify-center max-container:tw-pb-0 max-container:tw-pt-0 min-xxxl:tw-rounded-lg min-container:tw-bg-secondary-Latte">
                    <StaticImage
                      layout="constrained"
                      alt="Shorthand story image"
                      src="../../static/media/pricing/not-for-profit.png"
                      className="tr-relative -tw-left-[4px] tw-m-auto tw-h-full tw-overflow-hidden tw-rounded-r-lg tw-shadow-300 max-container:tw-rounded-lg min-container:tw-mr-14"
                      aria-hidden="true"
                      placeholder="dominantColor"
                    />
                  </div>
                </InfoSectionMediaContainer>
              </section>
              {/* Education discounts */}
              <section className="tw-flex tw-w-full tw-pb-[102px] tw-pt-[90px] max-container:!tw-flex-col-reverse max-container:!tw-items-center max-sm:tw-pt-[30px]">
                <InfoSection>
                  <Heading headerType={3} className={HEADING_THREE_STYLES}>
                    Teach visual <br />
                    <span className="tw-font-PassengerSerif !tw-font-semibold tw-italic">storytelling</span>
                  </Heading>
                  <InfoSectionTextContainer>
                    <InfoSectionParagraph>
                      We believe in equipping tomorrow's storytellers with the best tools today. The Shorthand for Classrooms program
                      offers free access to Shorthand, including publishing, for use in approved storytelling classes, empowering
                      students to master digital storytelling techniques and craft compelling narratives.
                    </InfoSectionParagraph>
                  </InfoSectionTextContainer>
                  <InfoSectionCtaContainer>
                    <button
                      data-tracking="enq-cta-02"
                      onClick={handleDiscountApplyClick}
                      className={"btn btn-secondary btn-inverse btn-lg !tw-m-0 !tw-font-bold max-xl:!tw-text-lg max-sm:!tw-text-sm"}
                    >
                      Apply for a discount
                    </button>
                  </InfoSectionCtaContainer>
                </InfoSection>
                <InfoSectionMediaContainer>
                  <div className="max-sm:!tw-rounded-0 max-container:!tw-bg-transparent tw-flex tw-w-full tw-justify-end tw-overflow-hidden  tw-pb-10 tw-pt-10 tw-pt-10 max-xxxl:tw-rounded-l-lg max-container:tw-pb-0 max-container:tw-pb-0 max-container:tw-pt-0 min-xxxl:tw-rounded-lg min-container:!tw-bg-brand_scale-950">
                    <StaticImage
                      layout="constrained"
                      alt="Shorthand story image"
                      src="../../static/media/pricing/education.png"
                      className="tw-m-auto tw-h-full tw-overflow-hidden tw-rounded-l-lg tw-shadow-300 max-container:tw-rounded-lg min-container:tw-ml-14"
                      aria-hidden="true"
                      placeholder="dominantColor"
                    />
                  </div>
                </InfoSectionMediaContainer>
              </section>
              <div ref={scrollTargetRef} />
              <FAQAccordionControlled
                handleFAQGetINTouchClick={handleFAQGetINTouchClick}
                value={defaultFaqOpen}
                handleChange={updateControlledSelect}
                heading="Frequently Asked Questions"
                questionGroup="pricing"
                theme="dark-theme"
              />
            </motion.div>
          </div>
        </main>
        <SignupCTAAlt />
        <Footer />
      </MotionConfig>
    </Layout>
  );
};

const PageHeading = (): JSX.Element => {
  return (
    <>
      Storytelling for freelancers <br />
      to the Fortune 500
    </>
  );
};
const PageSubHeading = (): JSX.Element => {
  return <>Create for free, pay when you&apos;re ready to publish</>;
};
const InlineLogo = (): JSX.Element => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={80}
      viewBox="0 0 990 755"
      fill="none"
      className="tw-mb-9 !tw-hidden tw-h-[65px] max-md:!tw-block"
    >
      <g className="tw-fill-tones-white" clipPath="url(#a)">
        <path d="M499.557.668C350.195.668 228.68 122.719 228.68 272.741c0 150.021 121.515 272.072 270.877 272.072 149.362 0 270.877-122.051 270.877-272.072C770.434 122.719 648.919.668 499.557.668Zm42.53 397.175c-17.215 3.56-43.543 4.577-61.77.509-13.671-2.543-25.822-7.629-36.455-14.748-3.037-2.034-8.101-5.085-12.151-4.577-7.595.509-12.151 11.188-15.696 16.274h-21.771v-90.522c7.088-1.017 14.177-1.525 21.771-2.034 4.557 38.65 29.366 60.517 64.302 67.637 18.733 4.068 42.53 3.051 57.719-3.56 15.19-6.611 25.822-21.868 23.797-42.21-1.013-8.645-7.595-14.747-14.177-18.307-10.632-5.594-24.303-8.137-37.973-10.171-55.188-9.154-111.895-16.274-113.92-79.842-1.519-46.786 31.391-69.671 74.934-71.705 28.353-1.526 49.112 2.543 67.339 11.697 5.57 2.542 12.152 8.136 18.734 7.628 8.101-.509 11.645-8.646 16.708-13.731h17.215v85.944c-7.595 1.017-14.683 2.035-22.278 3.052-4.557-31.53-19.24-51.872-46.581-61.535-26.834-9.662-74.427-7.628-85.566 14.748-3.544 7.628-4.051 19.325-.507 26.445 7.089 13.222 28.354 19.324 45.569 22.376 20.252 4.068 38.986 6.611 56.706 11.188 34.936 9.154 64.302 25.936 64.302 71.196 1.519 44.244-23.797 66.62-60.251 74.248ZM44.889 699.413c-2.025-7.119-8.101-10.171-13.67-10.171-5.57 0-8.608 3.052-8.608 6.103 0 5.085 7.595 7.119 17.215 10.679 11.645 4.069 23.797 10.68 23.797 24.411 0 14.239-12.152 24.41-30.38 24.41-16.201 0-27.846-9.154-32.91-23.902l17.721-4.577c2.026 6.103 7.595 11.697 15.696 11.697 6.582 0 9.62-3.051 9.62-7.12 0-6.102-9.114-7.628-20.252-12.205-9.114-3.56-20.253-8.645-20.253-22.376 0-12.205 12.152-23.393 29.366-23.393 14.683 0 26.835 9.154 30.379 21.359l-17.721 5.085ZM177.543 674.496v78.317h-18.734V722.3h-29.366v30.513h-18.734v-78.317h18.734v30.513h29.366v-30.513h18.734ZM265.135 672.969c22.277 0 40.505 17.29 40.505 40.684 0 23.393-18.228 40.683-40.505 40.683-22.278 0-40.505-17.29-40.505-40.683 0-23.394 18.227-40.684 40.505-40.684Zm0 63.06c12.657 0 21.265-9.154 21.265-22.376 0-13.223-8.608-22.377-21.265-22.377-12.658 0-20.759 9.154-20.759 22.377 0 13.222 8.101 22.376 20.759 22.376ZM395.763 752.813l-14.683-25.428h-9.62v25.428h-18.733v-78.317h28.859c19.746 0 31.392 10.68 31.392 26.953 0 10.68-5.063 18.308-14.177 22.376l17.215 28.988h-20.253Zm-23.797-42.21h13.164c6.583 0 9.114-5.086 9.114-9.154s-2.531-9.154-9.114-9.154h-13.164v18.308ZM522.847 674.496v17.799h-23.796v60.518h-18.734v-60.518h-23.29v-17.799h65.82ZM634.742 674.496v78.317h-18.733V722.3h-29.367v30.513h-18.733v-78.317h18.733v30.513h29.367v-30.513h18.733ZM732.46 740.607h-30.885l-4.556 12.206h-20.253l31.391-78.317h17.721l31.392 78.317h-20.253l-4.557-12.206Zm-6.075-16.273-5.57-15.257c-1.519-4.577-3.544-11.696-3.544-11.696s-2.025 7.119-3.544 11.696l-5.57 15.257h18.228ZM866.633 674.493v78.316h-17.72l-15.696-21.867c-7.088-9.663-17.721-24.919-17.721-24.919V752.3h-18.734v-78.316h17.721l16.709 22.376c5.063 6.611 16.708 23.394 17.214 23.394V673.984l18.227.509ZM949.668 674.496c22.278 0 40.505 15.765 40.505 39.667 0 23.901-18.227 38.65-40.505 38.65h-33.923v-78.317h33.923Zm0 61.026c11.139 0 20.759-7.628 20.759-21.359 0-13.731-9.62-21.868-20.759-21.868h-15.189v43.227h15.189Z" />
      </g>
      <defs>
        <clipPath id="a">
          <path fill="#fff" d="M.333.668h989.333v753.667H.333z" />
        </clipPath>
      </defs>
    </svg>
  );
};
